import React, { useState, useRef, useEffect } from 'react';

const FACE_API = process.env.REACT_APP_FACE_API || 'https://tribal-face-api.onrender.com';

// Each capture step tells the creator what pose to hold. The 3
// angles we collect give the matcher meaningful coverage against
// head rotation + expression variance without overwhelming the
// creator with a long wizard.
const STEPS = [
  { angle: 'front',   title: 'Front facing',            instruction: 'Look straight at the camera. Neutral expression.' },
  { angle: 'smile',   title: 'Smiling',                 instruction: 'Big natural smile showing teeth.' },
  { angle: 'profile', title: 'Side profile',            instruction: 'Turn your head 90° to either side — left or right, whichever feels easier.' },
];

export default function FaceProtect({ creatorId }) {
  const [stage, setStage] = useState('intro'); // intro | capturing | submitting | done
  const [stepIdx, setStepIdx] = useState(0);
  const [captures, setCaptures] = useState([]);     // [{ angle, dataUrl }]
  const [enrollStatus, setEnrollStatus] = useState(null);
  const [cameraErr, setCameraErr] = useState(null);
  const [username, setUsername] = useState('');
  const [scanResults, setScanResults] = useState(null);
  const [scanBusy, setScanBusy] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  async function startCamera() {
    setCameraErr(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setStage('capturing');
      setStepIdx(0);
      setCaptures([]);
      setEnrollStatus(null);
    } catch (e) {
      setCameraErr(e.message || 'could not access camera');
    }
  }

  function stopCamera() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }

  useEffect(() => () => stopCamera(), []);

  function captureFrame() {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return null;
    const w = video.videoWidth || 640;
    const h = video.videoHeight || 480;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    // Un-mirror the preview on grab. getUserMedia front-camera
    // streams are mirrored in CSS for UX, but the raw pixels are
    // not — draw them directly so the embedding matches a
    // normal-orientation photo of the same face.
    ctx.drawImage(video, 0, 0, w, h);
    return canvas.toDataURL('image/jpeg', 0.85);
  }

  function saveCurrentCapture() {
    const dataUrl = captureFrame();
    if (!dataUrl) return;
    const angle = STEPS[stepIdx].angle;
    setCaptures(prev => [...prev.filter(c => c.angle !== angle), { angle, dataUrl }]);
    if (stepIdx + 1 < STEPS.length) {
      setStepIdx(stepIdx + 1);
    } else {
      submit([...captures.filter(c => c.angle !== angle), { angle, dataUrl }]);
    }
  }

  function retakeStep(idx) {
    setStepIdx(idx);
    setCaptures(prev => prev.filter(c => c.angle !== STEPS[idx].angle));
  }

  async function submit(allCaptures) {
    setStage('submitting');
    try {
      const res = await fetch(`${FACE_API}/v2/face/enroll-multi`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          creator_id: creatorId,
          captures: allCaptures.map(c => ({ angle: c.angle, image_base64: c.dataUrl })),
        }),
      });
      const data = await res.json();
      setEnrollStatus(data);
    } catch (e) {
      setEnrollStatus({ success: false, error: e.message });
    }
    stopCamera();
    setStage('done');
  }

  async function scanFakes() {
    if (!username) return;
    setScanBusy(true);
    try {
      const res = await fetch(`${FACE_API}/v2/face/scan-fakes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ creator_id: creatorId, username }),
      });
      setScanResults(await res.json());
    } catch (e) {
      setScanResults({ success: false, error: e.message });
    }
    setScanBusy(false);
  }

  return (
    <div style={{ padding: 24, background: '#111', borderRadius: 12, color: '#fff', maxWidth: 640 }}>
      <h2 style={{ color: '#a78bfa', marginBottom: 6, fontSize: 22 }}>🛡️ Face Protect</h2>
      <p style={{ color: '#888', marginBottom: 24, fontSize: 14, lineHeight: 1.6 }}>
        Enroll your face to detect unauthorized use and fake accounts across the web.
        We capture three angles for a richer biometric profile than a single photo.
      </p>

      {/* ── Enrollment ── */}
      <div style={{ marginBottom: 28 }}>
        <h3 style={{ fontSize: 14, marginBottom: 12 }}>Step 1 — Enroll your face</h3>

        {stage === 'intro' && (
          <>
            <div style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.6, marginBottom: 14 }}>
              We'll walk you through 3 quick captures using your device camera. Takes about 15 seconds.
            </div>
            <button onClick={startCamera}
                    style={{ padding: '10px 20px', background: '#7c3aed', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
              Start enrollment
            </button>
            {cameraErr && (
              <div style={{ marginTop: 10, fontSize: 12, color: '#f87171' }}>
                Camera error: {cameraErr}
              </div>
            )}
          </>
        )}

        {(stage === 'capturing' || stage === 'submitting') && (
          <>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              {STEPS.map((s, i) => (
                <div key={s.angle} style={{
                  flex: 1, height: 4, borderRadius: 2,
                  background: i < stepIdx ? '#4ade80' : i === stepIdx ? '#7c3aed' : '#333',
                }} />
              ))}
            </div>

            <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 4 }}>
              {stepIdx + 1} of {STEPS.length} — <strong style={{ color: '#fff' }}>{STEPS[stepIdx].title}</strong>
            </div>
            <div style={{ fontSize: 12, color: '#cbd5e1', marginBottom: 12 }}>
              {STEPS[stepIdx].instruction}
            </div>

            <div style={{ position: 'relative', width: '100%', aspectRatio: '4/3', background: '#000', borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
              <video ref={videoRef} playsInline muted
                     style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }} />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
              <div style={{ position: 'absolute', inset: 0, border: '2px dashed rgba(167,139,250,0.4)', borderRadius: 8, pointerEvents: 'none' }} />
            </div>

            {stage === 'capturing' && (
              <button onClick={saveCurrentCapture}
                      style={{ padding: '10px 20px', background: '#7c3aed', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                Capture {STEPS[stepIdx].title.toLowerCase()}
              </button>
            )}
            {stage === 'submitting' && (
              <div style={{ fontSize: 13, color: '#a78bfa' }}>Uploading captures + extracting embeddings…</div>
            )}

            {captures.length > 0 && (
              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                {captures.map((c, idx) => (
                  <div key={c.angle} style={{ position: 'relative' }}>
                    <img src={c.dataUrl} alt={c.angle}
                         style={{ width: 70, height: 52, objectFit: 'cover', borderRadius: 4, border: '1px solid #333' }} />
                    <button onClick={() => retakeStep(STEPS.findIndex(s => s.angle === c.angle))}
                            style={{ position: 'absolute', top: 2, right: 2, padding: '1px 5px', fontSize: 9, background: 'rgba(0,0,0,0.7)', color: '#fff', border: 'none', borderRadius: 2, cursor: 'pointer' }}>
                      ↻
                    </button>
                    <div style={{ fontSize: 10, color: '#666', textAlign: 'center', marginTop: 2 }}>{c.angle}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {stage === 'done' && enrollStatus && (
          <div style={{ padding: 14, background: enrollStatus.success ? 'rgba(74,222,128,0.08)' : 'rgba(248,113,113,0.08)', borderRadius: 8, fontSize: 13 }}>
            {enrollStatus.success ? (
              <>
                <div style={{ color: '#4ade80', fontWeight: 600, marginBottom: 6 }}>
                  ✅ Enrolled {enrollStatus.count} angle{enrollStatus.count === 1 ? '' : 's'}
                </div>
                <div style={{ color: '#94a3b8', fontSize: 12 }}>
                  Angles stored: {(enrollStatus.angles_enrolled || []).join(' · ')}
                </div>
                {enrollStatus.per_angle_errors?.length > 0 && (
                  <div style={{ color: '#fbbf24', fontSize: 12, marginTop: 8 }}>
                    Skipped: {enrollStatus.per_angle_errors.map(e => `${e.angle} (${e.error})`).join(' · ')}
                  </div>
                )}
              </>
            ) : (
              <div style={{ color: '#f87171' }}>❌ {enrollStatus.error}</div>
            )}
            <button onClick={startCamera}
                    style={{ marginTop: 10, padding: '6px 14px', background: 'transparent', border: '1px solid #333', color: '#94a3b8', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
              Re-enroll
            </button>
          </div>
        )}
      </div>

      {/* ── Scan for fakes ── */}
      <div>
        <h3 style={{ fontSize: 14, marginBottom: 8 }}>Step 2 — Scan for fake accounts</h3>
        <input
          type="text"
          placeholder="Your username (e.g. TribalBBC)"
          value={username}
          onChange={e => setUsername(e.target.value)}
          style={{ width: '100%', padding: 10, borderRadius: 8, background: '#222', border: '1px solid #333', color: '#fff', marginBottom: 8, boxSizing: 'border-box' }}
        />
        <button onClick={scanFakes} disabled={scanBusy || !username}
                style={{ padding: '10px 20px', background: '#dc2626', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer' }}>
          {scanBusy ? 'Scanning…' : 'Scan for fakes'}
        </button>

        {scanResults && (
          <div style={{ marginTop: 12 }}>
            {scanResults.total_matches === 0 ? (
              <div style={{ padding: 10, background: 'rgba(74,222,128,0.08)', borderRadius: 8, fontSize: 13, color: '#4ade80' }}>
                ✅ No fake accounts found
              </div>
            ) : (
              <div>
                <div style={{ padding: 10, background: 'rgba(248,113,113,0.08)', borderRadius: 8, fontSize: 13, marginBottom: 8, color: '#f87171' }}>
                  ⚠️ {scanResults.total_matches} match(es) found — {scanResults.fake_accounts?.length || 0} potential fake account(s)
                </div>
                {(scanResults.findings || []).map((f, i) => (
                  <div key={i} style={{ padding: 10, background: '#1a1a2e', borderRadius: 8, fontSize: 12, marginBottom: 6 }}>
                    <div style={{ color: '#a78bfa' }}>{f.platform}</div>
                    <div style={{ color: '#888', wordBreak: 'break-all' }}>{f.page_url}</div>
                    <div style={{ color: '#22c55e' }}>
                      Confidence: {Math.round(f.confidence * 100)}%{f.matched_angle ? ` · matched ${f.matched_angle}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
